import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { StripedWordmark, TricolorStrip } from '../components/BSCAuthLayout';
import { Camera, X, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

/**
 * Camera-based QR scanner. On success, redirects to the appropriate route.
 * - Asset codes start with "AST-" → /scan/asset/<code>
 * - Generic URLs are followed as-is
 * - Fallback: manual code input
 */
export default function ScanQR() {
  const ref = useRef(null);
  const qrRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [manualCode, setManualCode] = useState('');
  const navigate = useNavigate();

  const onSuccess = (decodedText) => {
    try {
      if (qrRef.current) qrRef.current.stop().catch(() => {});
    } catch {}
    setScanning(false);
    if (decodedText.startsWith('http')) {
      window.location.href = decodedText;
    } else if (decodedText.startsWith('AST-')) {
      navigate(`/scan/asset/${decodedText}`);
    } else {
      navigate(`/scan/asset/${decodedText}`);
    }
  };

  const startCamera = async () => {
    setError(null);
    if (!ref.current) return;
    try {
      const q = new Html5Qrcode(ref.current.id);
      qrRef.current = q;
      await q.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        onSuccess,
        () => {} // ignore per-frame failures
      );
      setScanning(true);
    } catch (e) {
      setError(e?.message || 'Unable to access camera. Make sure you grant permission.');
    }
  };

  const stopCamera = async () => {
    if (qrRef.current) {
      try { await qrRef.current.stop(); } catch {}
      try { await qrRef.current.clear(); } catch {}
    }
    setScanning(false);
  };

  useEffect(() => () => { stopCamera(); }, []);

  const goManual = () => {
    if (!manualCode.trim()) return;
    navigate(`/scan/asset/${manualCode.trim().toUpperCase()}`);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F7F5F0' }}>
      <div className="border-b" style={{ backgroundColor: '#0A0A0A' }}>
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white/80" data-testid="back-link">
            <ArrowLeft size={14} /><span className="text-sm">Back</span>
          </Link>
          <StripedWordmark text="SCAN" size="sm" />
          <span className="w-8" />
        </div>
        <TricolorStrip />
      </div>

      <div className="flex-1 max-w-md mx-auto w-full p-6">
        <h1 className="text-2xl font-semibold mb-1" style={{ fontFamily: 'Manrope' }}>Asset QR Scanner</h1>
        <p className="text-sm mb-4" style={{ color: '#5C5C5C' }}>Point your camera at any BSC asset QR sticker.</p>

        <div id="qr-reader" ref={ref} className="rounded-lg overflow-hidden border bg-black" style={{ borderColor: '#E8E5DF', minHeight: 280 }} />

        {error && <p className="text-sm mt-3 p-3 rounded" style={{ backgroundColor: '#FEE', color: '#A43E33' }} data-testid="scan-error">{error}</p>}

        <div className="flex gap-2 mt-4">
          {!scanning ? (
            <Button onClick={startCamera} data-testid="start-scan" className="flex-1" style={{ backgroundColor: '#1E3B29', color: '#FFF' }}>
              <Camera size={14} className="mr-1" />Start Camera
            </Button>
          ) : (
            <Button onClick={stopCamera} data-testid="stop-scan" variant="outline" className="flex-1">
              <X size={14} className="mr-1" />Stop
            </Button>
          )}
        </div>

        <div className="mt-6 border-t pt-4" style={{ borderColor: '#E8E5DF' }}>
          <p className="text-xs uppercase tracking-wider mb-2" style={{ color: '#5C5C5C' }}>Or enter the code manually</p>
          <div className="flex gap-2">
            <Input placeholder="AST-2026-0001" value={manualCode} onChange={(e) => setManualCode(e.target.value)} data-testid="manual-code" />
            <Button onClick={goManual} data-testid="manual-go">Go</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
